/**
 * Query keys, in one place.
 *
 * Cache identity is a correctness concern here, not a tidiness one. The two layers are keyed
 * apart at the root (`config` versus `instance`) because they have different invalidation rules:
 * publishing a configuration version must never invalidate an instance, since a change to a
 * parent writes zero children (architecture §A.2), and a key tree that mixed them would make
 * that impossible to express.
 */
export const apiKeys = {
  config: {
    root: ['config'] as const,
    tarifs: () => [...apiKeys.config.root, 'tarifs'] as const,
    tarif: (slug: string) => [...apiKeys.config.tarifs(), slug] as const,
    generations: (slug: string) => [...apiKeys.config.tarif(slug), 'generations'] as const,
    generation: (slug: string, generation: string) =>
      [...apiKeys.config.generations(slug), generation] as const,
    versions: (slug: string, generation: string) =>
      [...apiKeys.config.generation(slug, generation), 'versions'] as const,
    version: (slug: string, generation: string, version: string) =>
      [...apiKeys.config.versions(slug, generation), version] as const,

    groupTarifs: () => [...apiKeys.config.root, 'group-tarifs'] as const,
    groupTarif: (slug: string) => [...apiKeys.config.groupTarifs(), slug] as const,
    groupGenerations: (slug: string) =>
      [...apiKeys.config.groupTarif(slug), 'generations'] as const,
    groupGeneration: (slug: string, generation: string) =>
      [...apiKeys.config.groupGenerations(slug), generation] as const,
    groupVersions: (slug: string, generation: string) =>
      [...apiKeys.config.groupGeneration(slug, generation), 'versions'] as const,
    groupVersion: (slug: string, generation: string, version: string) =>
      [...apiKeys.config.groupVersions(slug, generation), version] as const,
    groupCoreConfigs: (slug: string) =>
      [...apiKeys.config.groupTarif(slug), 'group-core-configs'] as const,

    /**
     * A tarif's assets, and the SET of them is keyed by the type filter.
     *
     * The filter is part of the key rather than a predicate over one cached page, because it is
     * part of the REQUEST (prompt 14.1): the service answers "this tarif's insurance cores" and
     * "this tarif's tarif & deckungs" as two different reads, and a console that filtered one
     * mixed page on a string prefix would be re-deriving a split the API already made.
     */
    coreConfigs: (slug: string, assetType: string) =>
      [...apiKeys.config.tarif(slug), 'core-configs', assetType] as const,
    coreConfig: (slug: string, name: string) =>
      [...apiKeys.config.tarif(slug), 'core-config', name] as const,

    /**
     * Everything DERIVED from one core config — the resolved view, the derived schema, the rows of
     * a range or a factor, the file bytes — lives under ONE sibling prefix, so a write invalidates
     * the asset and the prefix and nothing has to be listed (SDD-010 §5.7).
     *
     * A sibling of the asset key rather than a child of it, on purpose: the read-only checks
     * (`:validate`, `:validate-expression`) re-read the ASSET after every call — the SDK forgets a
     * resource's ETag after any successful mutation-shaped request, and the re-read is what
     * re-establishes it (§B.10) — and a re-read that also dropped the resolved view would refetch
     * every file's rows on every keystroke of an expression.
     */
    coreConfigDerived: (slug: string, name: string) =>
      [...apiKeys.config.tarif(slug), 'core-config-derived', name] as const,
    coreConfigResolved: (slug: string, name: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'resolved'] as const,
    coreConfigPolicyDataSchema: (slug: string, name: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'policy-data-schema'] as const,
    coreConfigNamedRange: (slug: string, name: string, code: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'named-range', code] as const,
    coreConfigNamedRangeFile: (slug: string, name: string, fileName: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'named-range-file', fileName] as const,
    coreConfigFactor: (slug: string, name: string, group: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'factor', group] as const,
    coreConfigFactorFile: (slug: string, name: string, group: string) =>
      [...apiKeys.config.coreConfigDerived(slug, name), 'factor-file', group] as const,
    /** The `insurance_core` asset schema itself — one per deployment, never per tarif. */
    coreConfigSchema: () => [...apiKeys.config.root, 'core-config-schema'] as const,
    /**
     * The billing rules a PIN PAIR resolves to (SDD-015 §5.5, B-7): the group override over the
     * child's total. Keyed by both ERNs — a different pair is a different answer.
     */
    resolvedBillingConfig: (tarifVersionErn: string, groupTarifVersionErn: string) =>
      [
        ...apiKeys.config.root,
        'resolved-billing-config',
        tarifVersionErn,
        groupTarifVersionErn,
      ] as const,
    /**
     * The coverage document a PIN PAIR resolves to (SDD-018 C-5): the group override over the
     * child's document, per section. Keyed by both ERNs, like the billing config.
     */
    resolvedTarifDeckung: (tarifVersionErn: string, groupTarifVersionErn: string) =>
      [
        ...apiKeys.config.root,
        'resolved-tarif-deckung',
        tarifVersionErn,
        groupTarifVersionErn,
      ] as const,
    /** The role catalog approval limits name (SDD-018 C-2) — one per deployment. */
    approvalRoles: () => [...apiKeys.config.root, 'approval-roles'] as const,
    groupCoreConfigsOfType: (slug: string, assetType: string) =>
      [...apiKeys.config.groupCoreConfigs(slug), assetType] as const,
    groupCoreConfig: (slug: string, name: string) =>
      [...apiKeys.config.groupTarif(slug), 'group-core-config', name] as const,
    /** The Partner domain's tag catalog (SDD-011 P-4) — one per deployment, never per tarif. */
    partnerRoles: () => [...apiKeys.config.root, 'partner-roles'] as const,
  },

  instance: {
    root: ['instance'] as const,
    /**
     * The list lives under its OWN segment rather than under `policies`, so that invalidating
     * every list after a write does not also invalidate every cached policy detail. A write to
     * one policy changes that policy and any list it appears in — and nothing about its
     * siblings (decision D7).
     */
    policyList: () => [...apiKeys.instance.root, 'policy-list'] as const,
    policies: (filter: Readonly<Record<string, string>>) =>
      [...apiKeys.instance.policyList(), filter] as const,
    /**
     * A policy read is a BELIEF about a date (SDD-008 §5): without `validAt` it is today's, and a
     * policy that starts later has none — so an as-of read keys separately, under the code, and an
     * invalidation of the code still reaches both.
     */
    policy: (code: string, validAt?: string, asOf?: string) =>
      validAt === undefined && asOf === undefined
        ? ([...apiKeys.instance.root, 'policies', code] as const)
        : ([
            ...apiKeys.instance.root,
            'policies',
            code,
            'asOf',
            validAt ?? '',
            asOf ?? '',
          ] as const),
    policySchema: (code: string, validAt?: string) =>
      [...apiKeys.instance.policy(code, validAt), 'schema'] as const,
    policyTimeline: (code: string) => [...apiKeys.instance.policy(code), 'timeline'] as const,
    /**
     * The slice list (SDD-017 §6 `/snapshots`): under the code, so a write to the policy drops it;
     * the system time is part of the key because an `asOf` read is a different answer.
     */
    policySnapshots: (code: string, asOf?: string) =>
      [...apiKeys.instance.policy(code), 'snapshots', asOf ?? 'now'] as const,
    /**
     * Coverage at a damage date (SDD-018 §7): under the code, so a write to the policy drops it; an
     * identifier lookup keys under its own segment (it may resolve to any policy).
     */
    coverageAtDate: (query: {
      readonly policyCode?: string;
      readonly identifier?: string;
      readonly damageDate: string;
      readonly asOf?: string;
    }) =>
      query.policyCode !== undefined && query.policyCode !== ''
        ? ([
            ...apiKeys.instance.policy(query.policyCode),
            'coverage',
            query.damageDate,
            query.asOf ?? 'now',
          ] as const)
        : ([
            ...apiKeys.instance.root,
            'coverage-by-identifier',
            query.identifier ?? '',
            query.damageDate,
            query.asOf ?? 'now',
          ] as const),
    /** Derived from the timeline (SDD-011 P-7); invalidated with it. */
    policyPartnerHistory: (code: string) =>
      [...apiKeys.instance.policy(code), 'partner-history'] as const,
    /**
     * Actual and estimated (SDD-021 PC-21): under the code, so a write to the policy drops it; the
     * business day is part of the key (`today` when absent — the service's default).
     */
    policyPremiumEstimate: (code: string, validAt?: string) =>
      [...apiKeys.instance.policy(code), 'premium-estimate', validAt ?? 'today'] as const,

    /**
     * Same split as the policy list, for the same reason: invalidating every Group Policy list
     * after a write must not also drop every cached Group Policy detail.
     */
    groupPolicyList: () => [...apiKeys.instance.root, 'group-policy-list'] as const,
    groupPolicies: (filter: Readonly<Record<string, string>>) =>
      [...apiKeys.instance.groupPolicyList(), filter] as const,
    groupPolicy: (code: string) => [...apiKeys.instance.root, 'group-policies', code] as const,
    /**
     * The member projection is a POINT-IN-TIME answer, so the business date belongs in the key:
     * two dates are two different answers and must not share a cache entry.
     *
     * The date is a trailing segment rather than part of the prefix, which is what lets every
     * `invalidates` below go on passing `groupPolicyMembers(code)` and still drop every date a
     * screen has looked at — TanStack matches invalidation by prefix.
     */
    groupPolicyMembers: (code: string, activeOn?: string) =>
      [
        ...apiKeys.instance.groupPolicy(code),
        'members',
        ...(activeOn === undefined ? [] : [activeOn]),
      ] as const,
    groupPolicyTimeline: (code: string) =>
      [...apiKeys.instance.groupPolicy(code), 'timeline'] as const,
    /**
     * The group premium summary (SDD-021 §8.1): under the group policy, so every write that drops
     * the members projection drops the summary too; the basis (a date, or a period) is the trailing
     * segment — two bases are two answers.
     */
    groupPolicyPremiumSummary: (code: string, basis: Readonly<Record<string, string>> = {}) =>
      [...apiKeys.instance.groupPolicy(code), 'premium-summary', basis] as const,
    groupPolicyPartnerHistory: (code: string) =>
      [...apiKeys.instance.groupPolicy(code), 'partner-history'] as const,
    /** The schema and roles a member enrolled under the agreement is validated against (P-8). */
    groupPolicyMemberSchema: (code: string) =>
      [...apiKeys.instance.groupPolicy(code), 'member-schema'] as const,

    /**
     * The group tarifs an agreement can be created under. Keyed under `instance` and not under
     * `config` on purpose: it is the OPERATIONS surface's own read (prompt 14.6), and a key in
     * the configuration tree would make a publish invalidate it — which is exactly the coupling
     * moving the discovery here was meant to remove.
     */
    availableGroupTarifs: () => [...apiKeys.instance.root, 'available-group-tarifs'] as const,

    /**
     * LEADS (SDD-013): the same list/detail split as policies, for the same reason. A lead is a
     * HEAD with an append-only history (T-24): every write moves the head and appends a version,
     * so a mutation invalidates the lead, its versions and the lists — never a policy, until
     * AcceptLead creates one (then the policy list and the master's members projection too).
     */
    leadList: () => [...apiKeys.instance.root, 'lead-list'] as const,
    leads: (filter: Readonly<Record<string, string>>) =>
      [...apiKeys.instance.leadList(), filter] as const,
    lead: (code: string) => [...apiKeys.instance.root, 'leads', code] as const,
    /** Through the LEAD's pin (SDD-013 §6.4); immutable configuration, cached forever. */
    leadSchema: (code: string) => [...apiKeys.instance.lead(code), 'schema'] as const,
    leadVersions: (code: string) => [...apiKeys.instance.lead(code), 'versions'] as const,
    /** The lead's actual and refreshed premium (SDD-021 PC-21); every lead write drops it. */
    leadPremiumEstimate: (code: string) =>
      [...apiKeys.instance.lead(code), 'premium-estimate'] as const,
    leadVersion: (code: string, version: string) =>
      [...apiKeys.instance.leadVersions(code), version] as const,
    leadImport: (jobId: string) => [...apiKeys.instance.root, 'lead-imports', jobId] as const,
    /** The tarifs a standalone lead can be opened under today — the twin of `availableGroupTarifs`. */
    availableTarifs: () => [...apiKeys.instance.root, 'available-tarifs'] as const,
  },

  /**
   * PARTNERS (SDD-014): the Partner domain's records, read and written through ITS API. A third
   * root beside `config` and `instance`, because a partner write must invalidate no policy — a
   * policy pins a partner's VERSION (PS-7) and reads the name at read time; what changes on a
   * partner edit is the partner and the lists it appears in, nothing the insurance layer caches.
   */
  partners: {
    root: ['partners'] as const,
    /** Same list/detail split as policies: a write drops every search, never every detail. */
    list: () => [...apiKeys.partners.root, 'list'] as const,
    search: (query: Readonly<Record<string, string>>) =>
      [...apiKeys.partners.list(), query] as const,
    partner: (code: string) => [...apiKeys.partners.root, 'partners', code] as const,
    versions: (code: string) => [...apiKeys.partners.partner(code), 'versions'] as const,
    /** The tenant's type catalog — what a create dialog offers and a role's `partnerTypes` names (PS-9). */
    types: () => [...apiKeys.partners.root, 'types'] as const,
    /** The tag catalog the roles are (SDD-011 P-3); the insurance layer has its own read of it (`config.partnerRoles`). */
    tags: () => [...apiKeys.partners.root, 'tags'] as const,
  },

  /**
   * CLAIMS (SDD-018 §9): claimservice's records, read and created through ITS API. A root of their
   * own: a claim write invalidates no policy (the claim is ABOUT a policy at a damage date), and a
   * policy write leaves the claims where they are — a claim records what was covered that day.
   */
  claims: {
    root: ['claims'] as const,
    /** Every list under one segment: a create drops every filtered page, never every claim. */
    list: () => [...apiKeys.claims.root, 'list'] as const,
    claim: (code: string) => [...apiKeys.claims.root, 'claims', code] as const,
  },

  /**
   * BILLING (SDD-015): the money the instance layer owes, read and written through the billing
   * service's API. A fourth root, because a billing write must invalidate no policy — an invoice
   * is a document ABOUT a policy's snapshot, priced under the pins in force (B-3), and issuing one
   * changes nothing the insurance layer caches. The reverse is also true: a policy write does not
   * drop these keys — the schedule and the estimate re-read on their own cadence, and a correction
   * on the policy reaches billing as an EVENT (or the reconcile run), never as a cache rule.
   */
  billing: {
    root: ['billing'] as const,
    /**
     * The same list/detail split as policies: every list under one segment, so a write drops
     * every filtered page and never every cached invoice.
     */
    invoiceList: () => [...apiKeys.billing.root, 'invoice-list'] as const,
    invoices: (query: Readonly<Record<string, string>>) =>
      [...apiKeys.billing.invoiceList(), query] as const,
    /**
     * Everything about ONE policy's billing — its own invoices, the master lines naming it, the
     * estimate — under a prefix shared by every policy, because an issue on a MEMBER writes the
     * master's invoice and every sibling's master lines (B-8): a write that cannot name the
     * policies it touched invalidates the prefix.
     */
    policies: () => [...apiKeys.billing.root, 'policies'] as const,
    policy: (policyCode: string) => [...apiKeys.billing.policies(), policyCode] as const,
    policyInvoices: (policyCode: string) =>
      [...apiKeys.billing.policy(policyCode), 'invoices'] as const,
    masterLines: (policyCode: string) =>
      [...apiKeys.billing.policy(policyCode), 'master-lines'] as const,
    estimate: (policyCode: string) => [...apiKeys.billing.policy(policyCode), 'estimate'] as const,
    memberEstimate: (policyCode: string) =>
      [...apiKeys.billing.policy(policyCode), 'member-estimate'] as const,
    invoice: (code: string) => [...apiKeys.billing.root, 'invoices', code] as const,
    bill: (code: string) => [...apiKeys.billing.root, 'bills', code] as const,
    /** The schedule (B-10): the row is keyed by POLICY, which is how every screen asks for it. */
    scheduleList: () => [...apiKeys.billing.root, 'schedule-list'] as const,
    schedule: (policyCode: string) => [...apiKeys.billing.scheduleList(), policyCode] as const,
    runList: () => [...apiKeys.billing.root, 'run-list'] as const,
    runs: (query: Readonly<Record<string, string>>) =>
      [...apiKeys.billing.runList(), query] as const,
    run: (code: string) => [...apiKeys.billing.root, 'runs', code] as const,
  },

  /**
   * THE LIST STANDARD (SDD-017 §9): the pages of a scroll, the filter catalog and the value
   * suggestions — each UNDER its collection's existing list prefix, so the invalidation every write
   * already does (`policyList()`, `invoiceList()` …) drops the open scroll too, and the scroll
   * restarts on a new cut. The page key holds filters, search, order and validAt — never `asOf`:
   * the first page pins it, and refresh() is what starts a new one.
   */
  /**
   * BACKGROUND JOBS (SDD-020 §6, T-43): one job per service and id, and the caller's own list per
   * service — the jobs tray merges the two services' lists, the wizard and the export button poll
   * one job. A create invalidates `mine` of its service.
   */
  jobs: {
    root: ['jobs'] as const,
    mine: (service: 'insurance' | 'billing' | 'data') => ['jobs', service, 'mine'] as const,
    job: (service: 'insurance' | 'billing' | 'data', id: string) =>
      ['jobs', service, 'job', id] as const,
  },

  /**
   * THE DATA SUITE (SDD-024): reports and custom datasets (heads, versions), the catalog snapshot,
   * the workspace settings, and queries. A report write invalidates its head and the list; a query
   * is keyed by its id and never invalidated (it is polled until final, then immutable).
   */
  reporting: {
    root: ['reporting'] as const,
    reportList: () => ['reporting', 'report-list'] as const,
    report: (code: string) => ['reporting', 'reports', code] as const,
    reportVersions: (code: string) => ['reporting', 'reports', code, 'versions'] as const,
    customDatasetList: () => ['reporting', 'custom-dataset-list'] as const,
    customDataset: (code: string) => ['reporting', 'custom-datasets', code] as const,
    catalog: () => ['reporting', 'catalog'] as const,
    dataset: (name: string) => ['reporting', 'catalog', name] as const,
    settings: () => ['reporting', 'settings'] as const,
    query: (id: string) => ['reporting', 'queries', id] as const,
  },

  lists: {
    pages: (prefix: readonly unknown[], query: Readonly<Record<string, string>>) =>
      [...prefix, 'pages', query] as const,
    catalog: (prefix: readonly unknown[], scope: string) =>
      [...prefix, 'filter-catalog', scope] as const,
    values: (prefix: readonly unknown[], scope: string, field: string, prefixText: string) =>
      [...prefix, 'filter-values', scope, field, prefixText] as const,
  },
} as const;
